import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ArrowRight, Zap, Lightbulb, TrendingUp, Users, Instagram, Mail, MapPin, ChevronDown, Rocket, Target, Heart, Code } from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';

// --- DATA SOURCE ---
const services = [
    {
      id: 'dev',
      title: 'Website Design and Development',
      description: 'At Vyxion Labs, we craft custom websites that combine stunning design with seamless functionality.',
      features: ['Custom Web Designs', 'SEO Optimization', 'Responsive Design', 'Security'],
      image: '/web_design.jpg',
      gradient: 'from-cyan-400 to-blue-500',
      link: '/web-development',
    },
    {
      id: 'revamp',
      title: 'Website Revamp',
      description: 'Give your website a fresh new look? Discover the benefits of our professional Website Revamp service.',
      features: ['Modern UI/UX Design', 'Performance Boost', 'Mobile Optimization', 'Brand Refresh'],
      image: '/web_revamp.jpg',
      gradient: 'from-purple-400 to-pink-500',
      link: '/website-revamp',
    },
    {
      id: 'hosting',
      title: 'Server Hosting & Maintenance',
      description: 'Reliable, secure hosting solutions with 24/7 monitoring and expert maintenance.',
      features: ['24/7 Server Monitoring', 'Security Updates', 'Content Revision', 'Technical Support'],
      image: '/server_hosting.jpg',
      gradient: 'from-emerald-400 to-teal-500',
      link: '/hosting-maintenance',
    },
];

const teamMembers = [
    {
        id: 1,
        name: 'Ram', 
        role: 'Chief Developer & Technical Lead',
        bio: 'Ram is the steady problem-solver on our team. He enjoys figuring things out, fixing what’s not working, and making sure everything runs smoothly. He aims to create something simple, clean, and useful.',
        image: '/ramphoto.jpg', 
        delay: '0ms'
    },
    {
        id: 2,
        name: 'Saef', 
        role: 'Design Director & Strategy Consultant',
        bio: 'Saef is the creative heartbeat of our team. He has a natural eye for design and a calm, thoughtful way of understanding what clients really want. He focuses on keeping designs clean, practical, and meaningful.',
        image: '/saefphoto.jpg', 
        delay: '150ms'
    },
];

// --- ANIMATION UTILITY ---
const AnimatedSection = ({ children, animationClass = 'animate-slideUp', delay = '0ms', className = '' }) => {
    const ref = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.unobserve(entry.target); 
                }
            },
            {
                root: null,
                rootMargin: '0px',
                threshold: 0.1,
            }
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => {
            if (ref.current) observer.unobserve(ref.current);
        };
    }, []);

    return (
        <div
            ref={ref}
            className={`${className} ${isVisible ? animationClass : 'opacity-0'}`}
            style={{ animationDelay: delay }}
        >
            {children}
        </div>
    );
};

// --- TEAM MEMBER COMPONENT (Mobile Optimized) ---
const AnimatedTeamMember = ({ member }) => {
    const ref = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.unobserve(entry.target);
                }
            },
            { threshold: 0.2 } 
        );

        if (ref.current) observer.observe(ref.current);
        return () => { if (ref.current) observer.unobserve(ref.current); };
    }, []);

    return (
        <div 
            ref={ref} 
            key={member.id} 
            className={`flex flex-col items-center text-center space-y-4 p-6 md:p-8 rounded-xl bg-white/50 backdrop-blur-sm border border-white/20 shadow-lg md:shadow-none transition-all duration-500 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
            style={{ transitionDelay: member.delay }} 
        >
            {/* Rounded Photo Style - Adjusted size for mobile */}
            <div className="w-40 h-40 md:w-56 md:h-56 rounded-full overflow-hidden shadow-xl border-4 border-cyan-500/50">
                <img 
                    src={member.image} 
                    alt={`Profile of ${member.name}`} 
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                />
            </div>
            <div className="pt-2">
                <h3 className="text-2xl font-bold text-slate-900">{member.name}</h3>
                <p className="text-cyan-600 font-bold uppercase tracking-wider text-xs md:text-sm mt-1">{member.role}</p>
            </div>
            <p className="text-slate-600 text-sm md:text-base max-w-sm leading-relaxed">{member.bio}</p>
        </div>
    );
};

// --- FOOTER SECTION ---
const FooterSection = () => {
    return (
        <footer className="bg-slate-900/90 backdrop-blur-sm text-slate-300 border-t border-slate-700/50 py-12 md:py-16 px-6">
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-8 md:gap-12 border-b border-slate-700 pb-12">
                    {/* Brand & Contact */}
                    <div className="sm:col-span-2 space-y-6">
                        <a href="/" className="flex items-center gap-3">
                            <img src="/logo.png" alt="SR logo" className="w-12 h-12 object-cover rounded-xl" />
                            <div>
                                <div className="text-xl font-bold text-white">Vyxion Labs</div>
                                <div className="text-sm text-slate-400">Digital Presence. Engineered.</div>
                            </div>
                        </a>
                        <p className="text-sm text-slate-400 max-w-xs">
                            We are committed to delivering outstanding digital solutions that combine stunning design with robust performance.
                        </p>
                        <div className="flex space-x-4 pt-2">
                            <a href="https://www.tiktok.com/@vyxion.labs" className="text-slate-400 hover:text-cyan-500 transition-colors" aria-label="TikTok"><FaTiktok size={20} /></a>
                            <a href="https://www.instagram.com/vyxionlabs" className="text-slate-400 hover:text-cyan-500 transition-colors" aria-label="Instagram"><Instagram size={20} /></a>
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div className="space-y-4">
                        <h4 className="text-lg font-bold text-white mb-4">Quick Links</h4>
                        <ul className="space-y-3">
                            <li><a href="/consult" className="text-sm hover:text-cyan-500 transition-colors">Book a Free Consult</a></li>
                            <li><a href="/pricing" className="text-sm hover:text-cyan-500 transition-colors">Pricing</a></li>
                            <li><a href="/ourworks" className="text-sm hover:text-cyan-500 transition-colors">Our Works</a></li>
                            <li><a href="/faq" className="text-sm hover:text-cyan-500 transition-colors">FAQ</a></li>
                            <li><a href="/about" className="text-sm hover:text-cyan-500 transition-colors">About Us</a></li>
                        </ul>
                    </div>

                    {/* Services */}
                    <div className="space-y-4">
                        <h4 className="text-lg font-bold text-white mb-4">Services</h4>
                        <ul className="space-y-3">
                            {services.map((s, index) => (
                                <li key={index}><a href={s.link} className="text-sm hover:text-cyan-500 transition-colors">{s.title}</a></li>
                            ))}
                        </ul>
                    </div>
                    
                    {/* Contact */}
                    <div className="space-y-4 col-span-1">
                        <h4 className="text-lg font-bold text-white mb-4">Contact</h4>
                        <ul className="space-y-3">
                            <li className="flex items-center gap-3">
                                <Mail size={16} className="text-cyan-500 flex-shrink-0" />
                                <a href="mailto:vyxionlabs.info@gmail.com" className="text-sm hover:text-cyan-500 transition-colors break-all">vyxionlabs.info@gmail.com</a>
                            </li>
                        </ul>
                    </div>
                </div>
                
                {/* Legal Links */}
                <div className="flex flex-col md:flex-row justify-center md:justify-start gap-4 md:space-x-6 mt-8 items-center md:items-start">
                    
                    <a href="/privacy" className="text-xs text-slate-500 hover:text-cyan-500 transition-colors font-medium">Privacy Policy</a>
                </div>

                <div className="mt-4 text-center text-sm text-slate-500">
                    &copy; {new Date().getFullYear()} Vyxion Labs. All rights reserved.
                </div>
            </div>
        </footer>
    );
};

// --- CTA BANNER ---
const CTABannerSection = () => {
    return (
        <AnimatedSection className="py-16 md:py-24 px-6 bg-slate-900/90" animationClass="animate-slideUp">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col lg:flex-row items-center justify-between p-8 md:p-12 rounded-3xl bg-gradient-to-r from-cyan-600 to-purple-700 shadow-2xl space-y-8 lg:space-y-0 lg:space-x-12 relative overflow-hidden">
                    <div className="max-w-xl text-center lg:text-left relative z-10">
                        <h2 className="text-3xl md:text-4xl font-black text-white leading-snug mb-3">
                            Ready to take your business <span className="italic font-serif">online?</span>
                        </h2>
                        <p className="text-lg text-slate-100/90">
                            Book a free, zero-obligation consultation to discuss your vision.
                        </p>
                    </div>

                    <a
                        href="/consult"
                        className="group relative px-8 py-4 bg-white text-slate-900 font-bold rounded-xl shadow-lg hover:shadow-cyan-300/50 hover:scale-105 transition-all text-lg overflow-hidden flex items-center gap-3 relative z-10"
                    >
                        <span className="relative z-10 flex items-center gap-2">
                            Start Your Project
                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </span>
                        <div className="absolute inset-0 shimmer" /> 
                    </a>
                    
                    {/* Decorative Circle */}
                    <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none" />
                </div>
            </div>
        </AnimatedSection>
    );
};

// --- MAIN PAGE COMPONENT ---
export default function AboutUsPage() {
    const [isContactPopupOpen, setIsContactPopupOpen] = useState(false);
    const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
    const servicesDropdownRef = useRef(null); 
    
    // Dropdown Logic
    const handleServicesClick = (e) => {
        e.stopPropagation(); 
        setIsServicesDropdownOpen(!isServicesDropdownOpen);
    };
    
    useEffect(() => {
        const handleClickOutside = (event) => {
        if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target)) {
            setIsServicesDropdownOpen(false);
        }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleMenuClick = () => {
        setIsContactPopupOpen(!isContactPopupOpen);
    };

    return (
        <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 text-slate-900 min-h-screen antialiased relative overflow-x-hidden">
        <style>{`
            @keyframes shimmer {
                0% { background-position: -1000px 0; }
                100% { background-position: 1000px 0; }
            }
            @keyframes slideInUp {
                0% { transform: translateY(30px); opacity: 0; }
                100% { transform: translateY(0); opacity: 1; }
            }
             @keyframes slideInLeft {
                0% { transform: translateX(-50px); opacity: 0; }
                100% { transform: translateX(0); opacity: 1; }
            }
            @keyframes slideInRight {
                0% { transform: translateX(50px); opacity: 0; }
                100% { transform: translateX(0); opacity: 1; }
            }
            .shimmer {
                background: linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent);
                background-size: 1000px 100%;
                animation: shimmer 3s infinite;
            }
            
            .modal-overlay {
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: rgba(0, 0, 0, 0.4);
                backdrop-filter: blur(8px);
                z-index: 9999; 
                opacity: 0;
                visibility: hidden;
                transition: opacity 0.3s ease, visibility 0.3s ease;
            }
            .modal-overlay.open {
                opacity: 1;
                visibility: visible;
            }
            .modal-content {
                position: fixed;
                top: 0;
                right: 0;
                width: 85%;
                max-width: 400px;
                height: 100%;
                background: rgba(255, 255, 255, 0.98);
                backdrop-filter: blur(30px);
                box-shadow: -10px 0 30px rgba(0, 0, 0, 0.2);
                transform: translateX(100%);
                transition: transform 0.4s cubic-bezier(0.86, 0, 0.07, 1);
                overflow-y: auto;
                padding: 2rem 1.5rem;
            }
            .modal-overlay.open .modal-content {
                transform: translateX(0);
            }
            .animate-slideUp { animation: slideInUp 0.8s ease-out forwards; }
            .animate-slideInLeft { animation: slideInLeft 0.8s ease-out forwards; }
            .animate-slideInRight { animation: slideInRight 0.8s ease-out forwards; }
        `}</style>

        {/* Mesh Background */}
  


        {/* --- Header --- */}
        <header className="w-full z-[100] border-b border-neutral-200 bg-white sticky top-0">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="/" className="flex items-center gap-3 z-50 opacity-100 fill-mode-forwards flex-shrink-0">
              <div className="relative w-10 h-10 md:w-14 md:h-14 flex items-center justify-center">
                <img src="/logo.png" alt="SR logo" className="w-full h-full object-cover rounded-sm" />
              </div>
              <div>
                <div className="text-lg font-bold text-neutral-900">Vyxion Labs</div>
                <div className="text-[10px] md:text-xs text-neutral-600 font-medium">Digital Presence. Engineered.</div>
              </div>
            </a>

            {/* Nav - Center - Flexbox Layout */}
            <nav className="hidden lg:flex flex-1 justify-center items-center gap-6 xl:gap-8  opacity-100 fill-mode-forwards px-4" style={{animationFillMode: 'forwards'}}>
                <a href="/" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">Home</a>
                <div className="relative" ref={servicesDropdownRef}>
                    <span id="services-link" className="text-neutral-700 hover:text-cyan-500 transition-colors font-medium cursor-pointer" onClick={handleServicesClick}>Services</span>
                    {isServicesDropdownOpen && (
                   <div id="services-dropdown" className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-64 bg-white rounded-sm border border-neutral-200 overflow-hidden z-[110]">
                   <div className="flex flex-col p-2">
                       <a href="/web-development" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">
                           Website Design & Development
                       </a>
                       <a href="/website-revamp" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">
                           Website Revamp
                       </a>
                       <a href="/hosting-maintenance" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">
                           Server Hosting & Maintenance
                       </a>
                   </div>
               </div>
               
                    )}
                </div>
                <a href="/pricing" className="text-neutral-700 hover:text-cyan-500 transition-colors font-medium">Pricing</a>
                <a href="/about" className="text-neutral-900 font-bold cursor-pointer">About Us</a>
                <a href="/ourworks" className="text-neutral-700 hover:text-cyan-500 transition-colors font-medium">Our Works</a>
                <a href="/faq" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">FAQ</a>
            </nav>

            {/* Right Controls */}
            <div className="flex items-center gap-3 md:gap-4 opacity-100 fill-mode-forwards flex-shrink-0" style={{animationFillMode: 'forwards'}}>
                <a href="/consult" className="hidden lg:flex group relative px-5 py-2 bg-gray-800 text-gray-200 font-semibold rounded-md hover:bg-gray-700 transition-all text-sm shadow-sm">
                  <span className="relative z-10 flex items-center gap-2 whitespace-nowrap">Free Consult <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                </a>
                <div className="flex items-center gap-3 border-l border-neutral-200 pl-3 md:pl-4">
                    <a href="https://www.tiktok.com/@vyxion.labs" target="_blank" rel="noopener noreferrer" className="text-neutral-600 hover:text-black transition-colors"><FaTiktok className="w-4 h-4" /></a>
                    <a href="https://www.instagram.com/vyxionlabs" target="_blank" rel="noopener noreferrer" className="text-neutral-600 hover:text-pink-600 transition-colors"><Instagram className="w-4 h-4" /></a>
                </div>
                <button className="lg:hidden flex items-center gap-1 px-3 py-2 text-neutral-700 font-semibold rounded-sm border border-neutral-300 hover:bg-neutral-100 transition-all text-sm bg-white ml-2" onClick={handleMenuClick}>
                    <Menu className="w-5 h-5" />
                </button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Contact Overlay */}
      <div className={`modal-overlay ${isContactPopupOpen ? 'open' : ''}`} onClick={handleMenuClick}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="absolute top-6 right-6 p-2 rounded-sm bg-neutral-100 hover:bg-neutral-200 transition-colors" onClick={handleMenuClick}>
                <X size={24} className="text-neutral-700" />
            </button>
            <h2 className="text-3xl font-bold text-neutral-900 mb-8 mt-12 animate-slideInLeft">Navigation</h2>
            <div className="space-y-4 mb-10 border-b border-neutral-200 pb-8">
                <div className="space-y-3 pl-4 border-l-2 border-purple-200 animate-slideInLeft delay-100">
                     <p className="text-xs font-bold text-neutral-400 uppercase tracking-wider">Services</p>
                     <a href="/web-development" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Web Development</a>
                     <a href="/website-revamp" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Website Revamp</a>
                     <a href="/hosting-maintenance" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Hosting</a>
                </div>
                <a href="/pricing" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-300" onClick={handleMenuClick}>Pricing</a>
                <a href="/" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-200" onClick={handleMenuClick}>Home</a>
                <a href="/faq" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-300" onClick={handleMenuClick}>FAQ</a>
                <a href="/about" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-400" onClick={handleMenuClick}>About Us</a>
                <a href="/consult" className="group relative block w-full text-center mt-12 px-6 py-4 bg-gray-800 text-gray-200 font-bold rounded-md hover:bg-gray-700 transition-all text-sm" onClick={handleMenuClick}>
              <span className="relative z-10 flex items-center justify-center gap-2">Start a Project Today <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></span>
            </a>
            </div>
            <h3 className="text-2xl font-bold text-neutral-900 mb-4">Get in Touch</h3>
            <div className="space-y-4 mb-10">
                <div className="flex items-start gap-4 p-4 rounded-sm border border-neutral-200 bg-neutral-50">
                    <Mail size={24} className="text-cyan-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <div className="text-lg font-bold text-neutral-800">Email</div>
                        <a href="mailto:vyxionlabs.info@gmail.com" className="text-neutral-600 hover:text-cyan-500 transition-colors text-base break-all">vyxionlabs.info@gmail.com</a>
                    </div>
                </div>
            </div>
            
        </div>
      </div>

        <main>
            {/* --- Team / Founders Section --- */}
            <section className="py-16 lg:py-24 px-6 ">
                <div className="max-w-7xl mx-auto">
                    <AnimatedSection className="text-center mb-16 space-y-4" animationClass="animate-slideUp">
                        <h2 className="text-3xl md:text-5xl font-black text-slate-900">
                            Meet the Engineers
                        </h2>
                        <p className="text-lg lg:text-xl text-slate-600 max-w-3xl mx-auto">
                            The blend of technical mastery and strategic design that defines Vyxion Labs.
                        </p>
                    </AnimatedSection>

                    {/* Individual Team Members */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-8">
                        {teamMembers.map((member) => (
                            <AnimatedTeamMember key={member.id} member={member} />
                        ))}
                    </div>
                </div>
            </section>
            

            {/* --- Backstory & Mission Section --- */}
            <section className="py-16 px-6">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
                    
                    {/* Left Column: Our Backstory */}
                    <AnimatedSection animationClass="animate-slideInLeft" delay="0ms">
                        <div className="space-y-6 lg:space-y-8">
                            <div className="flex items-center gap-3 text-xl font-bold text-purple-600">
                                <h2>Our Founding Story</h2>
                            </div>
                            <p className="text-base lg:text-lg text-slate-600 leading-relaxed">
                            Vyxion Labs began with two friends who shared a simple realisation — in an ever-changing digital world, businesses need more than just a website. They need a powerful online presence that evolves with technology and actually drives results.
                            <br /><br />
                            What started as us helping family members to create websites for their businessess soon became something much bigger. We saw firsthand how modern design, clean development, and the right digital strategy could generate new leads and transform small businesses.
                            </p>
                            <p className="text-base lg:text-lg text-slate-700 font-medium leading-relaxed border-l-4 border-cyan-500 pl-4 italic bg-slate-50 p-4 rounded-r-xl">
                                "From the beginning, our philosophy has been: to empower businesses with high-quality digital products that truly make an impact."
                            </p>
                        </div>
                    </AnimatedSection>

                    {/* Right Column: Our Mission */}
                    <AnimatedSection animationClass="animate-slideInRight" delay="0ms">
                        <div className="space-y-8">
                            <div className="flex items-center gap-3 text-xl font-bold text-cyan-600">
                                <h2>Our Core Mission</h2>
                            </div>
                            <ul className="space-y-8">
                                <li className="flex items-start gap-4">
                                    <Rocket className="w-8 h-8 text-cyan-500 flex-shrink-0 mt-1" />
                                    <div>
                                        <h3 className="text-xl font-bold text-slate-900 mb-1">Performance First</h3>
                                        <p className="text-slate-600 leading-relaxed">We focus on creating websites that perform where it matters most — clear messaging, strong user experience, and designs that convert visitors.</p>
                                    </div>
                                </li>
                                <li className="flex items-start gap-4">
                                    <Code className="w-8 h-8 text-cyan-500 flex-shrink-0 mt-1" />
                                    <div>
                                        <h3 className="text-xl font-bold text-slate-900 mb-1">Transparent Process</h3>
                                        <p className="text-slate-600 leading-relaxed">To demystify the web creation process, providing clear communication and collaboration from concept to launch.</p>
                                    </div>
                                </li>
                                <li className="flex items-start gap-4">
                                    <Lightbulb className="w-8 h-8 text-cyan-500 flex-shrink-0 mt-1" />
                                    <div>
                                        <h3 className="text-xl font-bold text-slate-900 mb-1">Lasting Partnership</h3>
                                        <p className="text-slate-600 leading-relaxed">To be a long-term digital partner, offering reliable support and maintenance long after the initial site goes live.</p>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </AnimatedSection>

                </div>
            </section>

            
            <CTABannerSection />
            <FooterSection />
        </main>
        </div>
    );
}